import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Muted,
  PageHeader,
  StatusBadge,
  Tabs,
} from '@/components/ui'
import {
  aprovarRequisicao,
  createRequisicao,
  fetchAtsDashboard,
  fetchCargosOpts,
  fetchDepartamentosOpts,
  fetchEmpresasRhAtivas,
  fetchRequisicoes,
  rejeitarRequisicao,
  solicitarInfoRequisicao,
} from './api'
import { KpiStrip } from './components/KpiStrip'
import type { PlanoTab, Requisicao } from './types'
import { statusTone } from './utils'

const PLANO_TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'solicitacoes', label: 'Solicitações' },
  { id: 'aprovacoes', label: 'Aprovações' },
] as const

function listFromPage(data: Awaited<ReturnType<typeof fetchRequisicoes>> | undefined): Requisicao[] {
  if (!data) return []
  if (Array.isArray((data as { dados?: Requisicao[] }).dados)) {
    return (data as { dados: Requisicao[] }).dados
  }
  return []
}

function empresaLabel(e: {
  nome: string
  nome_fantasia?: string
  principal?: boolean
  codigo_dominio?: string
}) {
  const nome = e.nome_fantasia && e.nome_fantasia !== e.nome
    ? `${e.nome} (${e.nome_fantasia})`
    : e.nome
  const cod = e.codigo_dominio ? ` · ${e.codigo_dominio}` : ''
  const pref = e.principal ? ' ★ preferencial' : ''
  return `${nome}${cod}${pref}`
}

export function PlanoContratacaoPage() {
  const { canEdit } = useAuth()
  const qc = useQueryClient()
  const [tab, setTab] = useState<PlanoTab>('solicitacoes')
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [formError, setFormError] = useState('')
  const [actionMsg, setActionMsg] = useState('')
  const [form, setForm] = useState({
    cargo: '',
    cargo_nome: '',
    departamento: '',
    empresa: '',
    tipo_contrato: 'clt',
    vagas_count: '1',
    prazo: '',
    prioridade: 'media',
    justificativa: '',
  })

  const dash = useQuery({
    queryKey: ['ats', 'dashboard'],
    queryFn: fetchAtsDashboard,
  })

  const requisicoes = useQuery({
    queryKey: ['ats', 'requisicoes', status, q],
    queryFn: () =>
      fetchRequisicoes({
        status: status || undefined,
        search: q || undefined,
      }),
  })

  const catalogs = useQuery({
    queryKey: ['ats', 'catalogs-plano'],
    queryFn: async () => {
      const [empresas, cargos, departamentos] = await Promise.all([
        fetchEmpresasRhAtivas(),
        fetchCargosOpts(),
        fetchDepartamentosOpts(),
      ])
      return { empresas, cargos, departamentos }
    },
    enabled: showForm,
  })

  // Preferencial (principal) como opção inicial ao abrir o formulário / carregar catálogo
  useEffect(() => {
    if (!showForm || !catalogs.data?.empresas?.length) return
    setForm((f) => {
      if (f.empresa) return f
      const pref =
        catalogs.data!.empresas.find((e) => e.principal)
        || catalogs.data!.empresas[0]
      return pref ? { ...f, empresa: pref.id } : f
    })
  }, [showForm, catalogs.data])

  const lista = listFromPage(requisicoes.data)
  const pendentes = useMemo(
    () => lista.filter((r) => r.status === 'pendente_aprov' || r.status === 'info_solicitada'),
    [lista],
  )

  const createMut = useMutation({
    mutationFn: createRequisicao,
    onSuccess: async () => {
      setShowForm(false)
      setFormError('')
      setActionMsg('Requisição criada.')
      setForm((f) => ({
        ...f,
        cargo: '',
        cargo_nome: '',
        departamento: '',
        empresa: '',
        justificativa: '',
        vagas_count: '1',
      }))
      await qc.invalidateQueries({ queryKey: ['ats', 'requisicoes'] })
      await qc.invalidateQueries({ queryKey: ['ats', 'dashboard'] })
    },
    onError: (err) => {
      setFormError(err instanceof ApiError ? err.message : 'Falha ao criar requisição.')
    },
  })

  const actionMut = useMutation({
    mutationFn: async (opts: {
      id: string
      kind: 'aprovar' | 'rejeitar' | 'info'
      text?: string
    }) => {
      if (opts.kind === 'aprovar') return aprovarRequisicao(opts.id)
      if (opts.kind === 'rejeitar') return rejeitarRequisicao(opts.id, opts.text || '')
      return solicitarInfoRequisicao(opts.id, opts.text || '')
    },
    onSuccess: async (res, vars) => {
      setActionMsg(
        vars.kind === 'aprovar'
          ? 'Requisição aprovada (vaga rascunho criada).'
          : vars.kind === 'rejeitar'
            ? 'Requisição rejeitada.'
            : 'Informações solicitadas.',
      )
      if (res && typeof res === 'object' && 'sucesso' in res && res.sucesso === false) {
        setActionMsg((res as { mensagem?: string }).mensagem || 'Ação não concluída.')
      }
      await qc.invalidateQueries({ queryKey: ['ats', 'requisicoes'] })
      await qc.invalidateQueries({ queryKey: ['ats', 'dashboard'] })
      await qc.invalidateQueries({ queryKey: ['ats', 'vagas'] })
    },
    onError: (err) => {
      setActionMsg(err instanceof ApiError ? err.message : 'Falha na ação.')
    },
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    setFormError('')
    const cargoNome =
      form.cargo_nome.trim() ||
      catalogs.data?.cargos.find((c) => c.id === form.cargo)?.nome ||
      ''
    if (!cargoNome) {
      setFormError('Informe o cargo / função.')
      return
    }
    if (!form.empresa) {
      setFormError('Selecione a empresa do grupo.')
      return
    }
    createMut.mutate({
      cargo: form.cargo || null,
      cargo_nome: cargoNome,
      departamento: form.departamento || null,
      empresa: form.empresa || null,
      tipo_contrato: form.tipo_contrato,
      vagas_count: Math.max(1, Number(form.vagas_count) || 1),
      prazo: form.prazo || null,
      prioridade: form.prioridade,
      justificativa: form.justificativa.trim(),
    })
  }

  function renderRow(r: Requisicao, withActions: boolean) {
    return (
      <tr key={r.id}>
        <td>
          <strong>{r.cargo_nome}</strong>
          <div className="hi-table-sub">{r.departamento_nome || '—'}</div>
        </td>
        <td>
          {r.empresa_nome || r.empresa_grupo_nome || '—'}
          {r.empresa_principal ? ' ★' : ''}
        </td>
        <td>
          <StatusBadge tone={statusTone(r.status)}>{r.status_display || r.status}</StatusBadge>
        </td>
        <td>{r.prioridade_display || r.prioridade || '—'}</td>
        <td>{r.vagas_count ?? 1}</td>
        <td>{r.solicitante_nome || '—'}</td>
        <td>
          {r.vaga ? (
            <Link to={`/app/recrutamento/${r.vaga}`}>{r.vaga_titulo || 'Abrir vaga'}</Link>
          ) : (
            '—'
          )}
        </td>
        {withActions ? (
          <td>
            {canEdit('plano-contratacao') || canEdit('recrutamento') ? (
              <div className="hi-row-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-xs"
                  disabled={actionMut.isPending}
                  onClick={() => actionMut.mutate({ id: r.id, kind: 'aprovar' })}
                >
                  Aprovar
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-xs"
                  disabled={actionMut.isPending}
                  onClick={() => {
                    const motivo = window.prompt('Motivo da rejeição:') || ''
                    if (!motivo.trim()) return
                    actionMut.mutate({ id: r.id, kind: 'rejeitar', text: motivo.trim() })
                  }}
                >
                  Rejeitar
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-xs"
                  disabled={actionMut.isPending}
                  onClick={() => {
                    const texto = window.prompt('O que precisa ser esclarecido?') || ''
                    if (!texto.trim()) return
                    actionMut.mutate({ id: r.id, kind: 'info', text: texto.trim() })
                  }}
                >
                  Pedir info
                </button>
              </div>
            ) : null}
          </td>
        ) : null}
      </tr>
    )
  }

  return (
    <section className="hi-page">
      <PageHeader
        title="Plano de contratação"
        subtitle="Solicitações de headcount, aprovações e geração de vaga rascunho via API. Entrevistas e IA seguem no legado."
        actions={
          <>
            {canEdit('plano-contratacao') || canEdit('recrutamento') ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowForm((v) => {
                    const next = !v
                    if (next) {
                      setForm((f) => ({ ...f, empresa: '' }))
                    }
                    return next
                  })
                }}
              >
                {showForm ? 'Fechar formulário' : '+ Nova requisição'}
              </button>
            ) : null}
            <Link className="btn btn-secondary" to="/app/recrutamento">
              Recrutamento
            </Link>
            <a className="btn btn-secondary" href={legacyAppUrl('#/plano-contratacao')}>
              Legado
            </a>
          </>
        }
      />

      {actionMsg ? <Alert tone="info">{actionMsg}</Alert> : null}

      {showForm ? (
        <form className="card card-body hi-stack" onSubmit={onSubmit}>
          <h4 className="card-title">Nova requisição</h4>
          {formError ? <Alert tone="danger">{formError}</Alert> : null}
          <div className="hi-form-grid">
            <label>
              Cargo existente (ativos com colaboradores)
              <select
                value={form.cargo}
                onChange={(e) => {
                  const id = e.target.value
                  if (!id) {
                    setForm((f) => ({ ...f, cargo: '', cargo_nome: '' }))
                    return
                  }
                  const c = catalogs.data?.cargos.find((x) => x.id === id)
                  setForm((f) => ({
                    ...f,
                    cargo: id,
                    cargo_nome: c?.nome || f.cargo_nome,
                    departamento: f.departamento || c?.departamento_id || '',
                  }))
                }}
              >
                <option value="">— cargo novo / não cadastrado —</option>
                {(catalogs.data?.cargos || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                    {c.headcount != null ? ` (${c.headcount})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Cargo / função *
              <input
                required
                value={form.cargo_nome}
                onChange={(e) => setForm((f) => ({ ...f, cargo_nome: e.target.value }))}
                placeholder={
                  form.cargo
                    ? 'Preenchido pelo cargo selecionado'
                    : 'Ex.: Analista de RH (cargo ainda não cadastrado)'
                }
              />
              <Muted>
                {form.cargo
                  ? 'Usando cargo do catálogo (ativos com headcount).'
                  : 'Informe um cargo novo se ele ainda não existir no catálogo.'}
              </Muted>
            </label>
            <label>
              Departamento
              <select
                value={form.departamento}
                onChange={(e) => setForm((f) => ({ ...f, departamento: e.target.value }))}
              >
                <option value="">—</option>
                {(catalogs.data?.departamentos || []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.nome}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Empresa do grupo *
              <select
                required
                value={form.empresa}
                onChange={(e) => setForm((f) => ({ ...f, empresa: e.target.value }))}
                disabled={catalogs.isLoading}
              >
                {catalogs.isLoading ? (
                  <option value="">Carregando empresas…</option>
                ) : (
                  <>
                    <option value="">Selecione a empresa…</option>
                    {(catalogs.data?.empresas || []).map((e) => (
                      <option key={e.id} value={e.id}>
                        {empresaLabel(e)}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </label>
            <label>
              Tipo de contrato
              <select
                value={form.tipo_contrato}
                onChange={(e) => setForm((f) => ({ ...f, tipo_contrato: e.target.value }))}
              >
                <option value="clt">CLT</option>
                <option value="pj">PJ</option>
                <option value="estagio">Estágio</option>
                <option value="temporario">Temporário</option>
                <option value="aprendiz">Aprendiz</option>
              </select>
            </label>
            <label>
              Quantidade
              <input
                type="number"
                min={1}
                value={form.vagas_count}
                onChange={(e) => setForm((f) => ({ ...f, vagas_count: e.target.value }))}
              />
            </label>
            <label>
              Prazo
              <input
                type="date"
                value={form.prazo}
                onChange={(e) => setForm((f) => ({ ...f, prazo: e.target.value }))}
              />
            </label>
            <label>
              Prioridade
              <select
                value={form.prioridade}
                onChange={(e) => setForm((f) => ({ ...f, prioridade: e.target.value }))}
              >
                <option value="baixa">Baixa</option>
                <option value="media">Média</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </label>
            <label className="hi-span-all">
              Justificativa
              <textarea
                rows={3}
                value={form.justificativa}
                onChange={(e) => setForm((f) => ({ ...f, justificativa: e.target.value }))}
              />
            </label>
          </div>
          <div className="hi-actions">
            <button type="submit" className="btn btn-primary" disabled={createMut.isPending}>
              {createMut.isPending ? 'Salvando…' : 'Enviar solicitação'}
            </button>
          </div>
        </form>
      ) : null}

      <Tabs
        items={PLANO_TABS}
        value={tab}
        onChange={setTab}
        label="Abas do plano"
      />

      {tab === 'dashboard' ? (
        <div className="hi-stack">
          {dash.isLoading ? <Muted>Carregando…</Muted> : null}
          {dash.data ? <KpiStrip data={dash.data} /> : null}
          <Muted>
            Pendentes de aprovação: {dash.data?.solicitacoes_pendentes ?? pendentes.length}
          </Muted>
        </div>
      ) : null}

      {tab === 'solicitacoes' || tab === 'aprovacoes' ? (
        <div className="hi-stack">
          {tab === 'solicitacoes' ? (
            <form
              className="hi-filters"
              onSubmit={(e) => {
                e.preventDefault()
                setQ(search.trim())
              }}
            >
              <input
                type="search"
                placeholder="Buscar cargo, depto, justificativa…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Todos os status</option>
                <option value="pendente_aprov">Pendente</option>
                <option value="info_solicitada">Info solicitada</option>
                <option value="aprovado">Aprovado</option>
                <option value="rejeitado">Rejeitado</option>
                <option value="em_andamento">Em andamento</option>
              </select>
              <button type="submit" className="btn btn-primary">
                Filtrar
              </button>
            </form>
          ) : null}

          {requisicoes.isError ? (
            <Alert tone="danger">
              {requisicoes.error instanceof ApiError
                ? requisicoes.error.message
                : 'Falha ao listar requisições.'}
            </Alert>
          ) : null}

          <DataTable>
            <thead>
              <tr>
                <th>Cargo</th>
                <th>Empresa</th>
                <th>Status</th>
                <th>Prioridade</th>
                <th>Qtd</th>
                <th>Solicitante</th>
                <th>Vaga</th>
                {tab === 'aprovacoes' ? <th>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {(tab === 'aprovacoes' ? pendentes : lista).map((r) =>
                renderRow(r, tab === 'aprovacoes'),
              )}
            </tbody>
          </DataTable>
          {requisicoes.isLoading ? <Muted>Carregando…</Muted> : null}
          {!requisicoes.isLoading && (tab === 'aprovacoes' ? pendentes : lista).length === 0 ? (
            <Empty>Nenhuma requisição neste filtro.</Empty>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

export default PlanoContratacaoPage
